// OpenAIRealtimeService
//
// Browser-side client for OpenAI Realtime over the WORKER-RELAYED
// WEBSOCKET transport (LUM-172 transport pivot). Public surface
// (start/stop/device setters/callbacks) is consumed by
// useOpenAIRealtimeCall.
//
// Why not WebRTC: OpenAI's /v1/realtime/calls transport clears its
// server-managed output audio buffer at input_audio_buffer.speech_started
// — unconditionally, before transcription, regardless of
// interrupt_response:false (proved by event_log forensics on live
// calls). For a backchannel-heavy interview that mutes the interviewer
// on every "mm-hmm". Over the relay the client OWNS playback
// (RealtimeAudioEngine), so nothing server-side can touch it — and the
// browser now runs the exact transport the Tier-2 eval suite validates.
//
// Connection flow:
//   1. RealtimeAudioEngine.start() → mic capture + playback pipeline
//   2. WebSocket to /api/realtime/ws?session_id&token&build — the worker
//      authenticates, writes the registry row, and hands the socket to
//      the per-call relay Durable Object (server-side persistence)
//   3. Relay signals `dd.relay.ready` with our call id → greeting via
//      response.create
//   4. Mic chunks stream up as input_audio_buffer.append; audio deltas
//      stream down into the engine; the RealtimeTurnArbiter decides all
//      turn policy (backchannel no-op / debounced reply / interruption)
//
// The API key never reaches the browser; auth is the user's Supabase
// token, validated by the worker.

import { apiFetch } from '@/lib/api-client';
import { RealtimeAudioEngine } from '@/services/realtime-audio';
import {
  CLIENT_TURN_POLICY_BUILD,
  isVocabularyEcho,
  RealtimeTurnArbiter,
  type TurnDecision,
} from '@/services/realtime-barge-in';
import { markedTopicIdFromEvent } from '@/services/realtime-mark-topic';
import { logger } from '@/utils/logger';

export interface RealtimeCallStartData {
  callId?: string;
}

export interface RealtimeCallEndData {
  reason?: string;
}

export interface RealtimeTranscript {
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
}

export interface RealtimeErrorData {
  message?: string;
  errorMsg?: string;
  error?: { type?: string; message?: string };
}

/** Mid-call transport state, surfaced so the UI can show a
 *  "reconnecting…" affordance during the resume window (LUM-176 #3). */
export type RealtimeConnectionState = 'reconnecting' | 'reconnected';

/**
 * Deterministic agent presence for the in-call UI (LUM-133): tier-1
 * signals only — client-owned playback and reply-commitment state,
 * never transcript inference.
 *   listening → default; also after playback ends
 *   thinking  → a reply is committed (response.create sent) but no
 *               audio is audible yet
 *   speaking  → audible playback in progress
 */
export type RealtimeAgentState = 'listening' | 'thinking' | 'speaking';

export interface RealtimeEventCallbacks {
  onCallStart?: (data: RealtimeCallStartData) => void;
  onCallEnd?: (data: RealtimeCallEndData) => void;
  onTranscript?: (transcript: RealtimeTranscript) => void;
  onError?: (error: RealtimeErrorData) => void;
  onConnectionState?: (state: RealtimeConnectionState) => void;
  onAgentState?: (state: RealtimeAgentState) => void;
  /** A mark_topic call completed `topicId` (LUM-134). Fires only when
   *  the worker registered the tool; the relay owns the ack. */
  onTopicMarked?: (topicId: string) => void;
  /** Level of each mic capture chunk (LUM-82) — a number on the
   *  shared mic scale, never audio. */
  onMicLevel?: (level: number) => void;
}

export interface RealtimeStartResult {
  id?: string;
}

const WS_ENDPOINT = '/api/realtime/ws';

/** Mic backlog on ws.bufferedAmount above this = the uplink cannot
 *  drain ~4s of audio; stop queueing more (LUM-176 #2). */
const BACKPRESSURE_MAX_BUFFERED_BYTES = 256 * 1024;

/** Backlog sustained this long = a dead link pretending to be open —
 *  force the socket closed so the reconnect path takes over. */
const BACKPRESSURE_STALL_MS = 8_000;

/** Resume dials after an abnormal socket loss (LUM-176 #3) run until
 *  this absolute deadline — just under the relay's 45s reconnect
 *  grace. A fixed attempt count proved wrong (LUM-187): one slow
 *  proxied handshake could eat the budget while the grace window
 *  still had 30s to give. */
const RESUME_DEADLINE_MS = 40_000;
const RECONNECT_BASE_DELAY_MS = 1_000;
/** Cap between dials so a long outage still probes several times
 *  inside the deadline. */
const RECONNECT_MAX_DELAY_MS = 8_000;
const RELAY_READY_TIMEOUT_MS = 15_000;
const RESUME_READY_TIMEOUT_MS = 8_000;
/** Ticket mint must never eat the resume budget (LUM-187): an
 *  unbounded fetch on a flaky VPN could consume the whole grace. */
const TICKET_MINT_TIMEOUT_MS = 5_000;

/** The relay pings dd.relay.ping every ~15s. No downlink frames for
 *  this long on an active call = a half-open DOWNLINK (the uplink
 *  case is covered by the backpressure stall) — force-close so the
 *  resume path dials fresh (LUM-187). */
const DOWNLINK_STALL_MS = 40_000;
const DOWNLINK_CHECK_INTERVAL_MS = 10_000;
/** Mic chunks arrive every ~100ms from a healthy capture path; a gap
 *  this long means capture died silently (LUM-187 pass 2). */
const MIC_STALL_MS = 15_000;

/** Relay's app close code for "the call died on MY side" (upstream
 *  OpenAI socket loss/error) — distinct from 1000 so a relay failure
 *  surfaces as an error, not a silent wrap-up. Mirrored in
 *  worker/lib/realtime/observer.ts (separate bundles, keep in sync). */
const RELAY_FAILURE_CLOSE_CODE = 4011;

const FRIENDLY_START_ERROR =
  'Voice connection error. Please try again. If this persists, contact dialogue-help@luminarium.ai';

function delay(ms: number): Promise<void> {
  const { promise, resolve } = Promise.withResolvers<void>();
  setTimeout(resolve, ms);
  return promise;
}

/** Resolve on the browser's `online` event, or after `maxMs`. */
function awaitOnline(maxMs: number): Promise<void> {
  if (maxMs <= 0 || navigator.onLine) return Promise.resolve();
  const { promise, resolve } = Promise.withResolvers<void>();
  const done = () => {
    clearTimeout(timer);
    window.removeEventListener('online', done);
    resolve();
  };
  const timer = setTimeout(done, maxMs);
  window.addEventListener('online', done);
  return promise;
}

export class OpenAIRealtimeService {
  private ws: WebSocket | null = null;
  private engine: RealtimeAudioEngine | null = null;
  private callbacks: RealtimeEventCallbacks = {};
  private isInitialized = false;
  private callId: string | null = null;
  private callStartFired = false;
  private callEndFired = false;
  private inputDeviceId: string | undefined;
  private sessionId: string | null = null;
  /** Monotonic id correlating scheduled/fired/cancelled response.create
   *  telemetry (LUM-176 #5). */
  private decisionSeq = 0;
  /** Mic chunks dropped under backpressure this call. */
  private micDropped = 0;
  /** Wall time the backpressure threshold was first crossed; 0 = clear. */
  private backpressureSince = 0;
  private lastBackpressureTelemetry = 0;
  /** Last presence state emitted to the UI (LUM-133); deduped. */
  private agentState: RealtimeAgentState = 'listening';
  /** True while the resume loop owns socket lifecycle. */
  private resuming = false;
  /** Wall time of the last frame received from the relay (LUM-187). */
  private lastDownlinkAt = 0;
  private downlinkWatchdog: number | null = null;
  /** Wall time of the last mic chunk from the engine (LUM-187 p2). */
  private lastMicChunkAt = 0;
  private micStallReported = false;
  /** Resume dial awaiting dd.relay.ready — owned here so teardown can
   *  close it (user hangs up mid-resume; Codex crosscheck finding 8). */
  private pendingResumeWs: WebSocket | null = null;
  private outputDeviceId: string | undefined;
  private partialAssistantTranscript = '';
  private bargeInGate = new RealtimeTurnArbiter();
  /** Cancels the debounced response.create, if one is scheduled. */
  private cancelScheduledResponse: (() => void) | null = null;

  /** Effective STT vocabulary prompt, from dd.relay.ready — the echo
   *  detector compares transcriptions against it. */
  private effectiveVocabulary = '';

  get ready(): boolean {
    return this.isInitialized;
  }

  initialize(callbacks: RealtimeEventCallbacks = {}): void {
    if (typeof window === 'undefined') {
      logger.warn(
        'OpenAIRealtimeService: cannot initialize in non-browser environment',
      );
      return;
    }
    this.callbacks = callbacks;
    this.isInitialized = true;
  }

  updateCallbacks(callbacks: Partial<RealtimeEventCallbacks>): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  // The worker owns the session config. sessionId is REQUIRED on this
  // transport: it keys the relay's registry row and server-side
  // persistence.
  async start(sessionId: string): Promise<RealtimeStartResult> {
    if (!this.isInitialized) {
      throw new Error('OpenAIRealtimeService not initialized');
    }
    if (this.ws) {
      logger.warn('OpenAIRealtimeService: start() called while already active');
      return { id: this.callId ?? undefined };
    }
    if (!sessionId) {
      throw new Error('OpenAIRealtimeService: sessionId required');
    }

    // apiFetch attaches the Supabase bearer token; a missing session
    // surfaces as a 401 on the ticket mint below.

    this.callId = null;
    this.sessionId = sessionId;
    this.callStartFired = false;
    this.callEndFired = false;
    this.partialAssistantTranscript = '';
    this.bargeInGate = new RealtimeTurnArbiter();
    this.cancelScheduledResponse?.();
    this.cancelScheduledResponse = null;
    this.decisionSeq = 0;
    this.micDropped = 0;
    this.backpressureSince = 0;
    this.lastBackpressureTelemetry = 0;
    this.resuming = false;

    const engine = new RealtimeAudioEngine();
    this.engine = engine;
    try {
      await engine.start(this.inputDeviceId);
    } catch (err) {
      // start() can fail AFTER acquiring the mic (e.g. worklet module
      // rejection) — release tracks/context or the mic indicator stays
      // lit until tab close (PR 414 review). stop() is idempotent and
      // safe after a partial start.
      this.engine = null;
      engine.stop();
      throw err;
    }
    if (this.outputDeviceId) engine.setOutputDevice(this.outputDeviceId);

    // Ticket-based WS auth: mint a 60s single-purpose credential under
    // normal bearer auth so the user's access token never appears in a
    // URL (edge logs, devtools, proxies).
    const ticket = await this.mintTicket(sessionId);
    if (ticket === null) {
      this.engine = null;
      engine.stop();
      throw new Error('Realtime ticket failed');
    }

    const ws = new WebSocket(this.buildWsUrl(sessionId, ticket, null));
    this.ws = ws;

    engine.onMicChunk = b64 => {
      this.lastMicChunkAt = Date.now();
      this.sendMicChunk(b64);
    };
    engine.onMicLevel = level => this.callbacks.onMicLevel?.(level);
    // Mic lifecycle (LUM-187 pass 2): a dead track/context is
    // indistinguishable from a silent user server-side — record it in
    // forensics and, when recovery failed, tell the user; the call
    // itself stays alive (they may replug).
    engine.onMicEvent = (kind, detail) => {
      logger.warn('OpenAIRealtimeService: mic event', { kind, detail });
      this.send({ type: 'dd.client.mic_event', kind, detail });
      if (kind === 'lost') {
        this.callbacks.onError?.({
          message:
            'Microphone connection lost. Please check your mic or headset — the interview is still running.',
        });
      }
    };
    // Synthesize the playback-state events the turn arbiter keys on —
    // on this transport playback truth lives in OUR engine, not the
    // server.
    engine.onPlaybackStarted = () => {
      this.setAgentState('speaking');
      this.feedArbiter({ type: 'output_audio_buffer.started' });
    };
    engine.onPlaybackStopped = () => {
      this.setAgentState('listening');
      this.feedArbiter({ type: 'output_audio_buffer.stopped' });
    };
    // Starvation forensics (LUM-176 #1): every underrun lands in the
    // call's event_log with the gap and the widened jitter lead.
    engine.onUnderrun = info => {
      logger.debug('OpenAIRealtimeService: playback underrun', { ...info });
      this.send({
        type: 'dd.client.underrun',
        item_id: info.itemId,
        ms: info.gapMs,
        n: info.count,
      });
    };

    this.wireSocket(ws);

    // The relay signals readiness with our call id once OpenAI's
    // session exists; the greeting fires from that handler. Wait for it
    // so callers get the real call id.
    const callId = await this.awaitRelayReady(RELAY_READY_TIMEOUT_MS);
    this.onRelayReady = null;
    if (!callId) {
      this.teardown(undefined);
      throw new Error('Realtime relay did not become ready');
    }
    this.lastDownlinkAt = Date.now();
    this.startDownlinkWatchdog();
    this.startVisibilityCheck();
    return { id: callId };
  }

  /**
   * Half-open DOWNLINK detection (LUM-187): the relay pings every
   * ~15s, so a live socket never goes DOWNLINK_STALL_MS without a
   * frame. The uplink case is covered by the backpressure stall —
   * this is its mirror for middleboxes that kill only the return
   * path. Force-closing routes recovery through the resume loop.
   */
  private startDownlinkWatchdog(): void {
    this.stopDownlinkWatchdog();
    this.lastMicChunkAt = Date.now();
    this.downlinkWatchdog = window.setInterval(() => {
      const ws = this.ws;
      if (!ws || this.resuming || !this.callStartFired) return;
      // Uplink capture stall (LUM-187 pass 2): chunks arrive every
      // ~100ms from a healthy worklet, so a long gap means capture
      // died for a reason the track/context handlers did not catch.
      // Forensics only — the mic recovery paths own the fix.
      if (
        !this.micStallReported &&
        Date.now() - this.lastMicChunkAt > MIC_STALL_MS
      ) {
        this.micStallReported = true;
        logger.warn('OpenAIRealtimeService: mic chunk stall');
        this.send({ type: 'dd.client.mic_event', kind: 'stall' });
      }
      if (Date.now() - this.lastDownlinkAt <= DOWNLINK_STALL_MS) return;
      logger.warn('OpenAIRealtimeService: downlink stall', {
        quietMs: Date.now() - this.lastDownlinkAt,
      });
      try {
        ws.close(4008, 'downlink-stall');
      } catch {
        // close raced; the close handler drives recovery either way
      }
    }, DOWNLINK_CHECK_INTERVAL_MS);
  }

  private stopDownlinkWatchdog(): void {
    if (this.downlinkWatchdog !== null) {
      clearInterval(this.downlinkWatchdog);
      this.downlinkWatchdog = null;
    }
    if (this.visibilityCheck) {
      document.removeEventListener('visibilitychange', this.visibilityCheck);
      this.visibilityCheck = null;
    }
  }

  /** Background tabs throttle intervals (mobile suspends them
   *  outright), so the watchdog can sleep straight past a dead socket
   *  AND the relay's 45s grace (LUM-187 pass 2, Codex finding 4).
   *  Reconcile the moment the tab is visible again: a stale downlink
   *  force-closes NOW and the resume loop dials immediately. */
  private visibilityCheck: (() => void) | null = null;

  private startVisibilityCheck(): void {
    if (this.visibilityCheck) return;
    this.visibilityCheck = () => {
      if (document.visibilityState !== 'visible') return;
      const ws = this.ws;
      if (!ws || this.resuming || !this.callStartFired) return;
      if (Date.now() - this.lastDownlinkAt <= DOWNLINK_STALL_MS) return;
      logger.warn('OpenAIRealtimeService: stale downlink on tab return');
      try {
        ws.close(4008, 'downlink-stall');
      } catch {
        // close raced
      }
    };
    document.addEventListener('visibilitychange', this.visibilityCheck);
  }

  private onRelayReady: ((callId: string) => void) | null = null;

  /** Mint a 60s single-purpose WS credential; null on failure. Bounded
   *  (LUM-187): an unbounded fetch inside the resume loop could eat
   *  the relay's whole 45s grace on a stalled VPN. */
  private async mintTicket(sessionId: string): Promise<string | null> {
    try {
      const res = await apiFetch('/api/realtime/ticket', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
        signal: AbortSignal.timeout(TICKET_MINT_TIMEOUT_MS),
      });
      if (!res.ok) {
        logger.warn('OpenAIRealtimeService: ticket mint failed', {
          status: res.status,
        });
        return null;
      }
      const { ticket } = (await res.json()) as { ticket: string };
      return ticket;
    } catch (err) {
      logger.warn('OpenAIRealtimeService: ticket mint threw', { err });
      return null;
    }
  }

  private buildWsUrl(
    sessionId: string,
    ticket: string,
    resumeCallId: string | null,
  ): URL {
    const wsUrl = new URL(WS_ENDPOINT, window.location.origin);
    wsUrl.protocol = wsUrl.protocol === 'https:' ? 'wss:' : 'ws:';
    wsUrl.searchParams.set('session_id', sessionId);
    wsUrl.searchParams.set('ticket', ticket);
    wsUrl.searchParams.set('build', CLIENT_TURN_POLICY_BUILD);
    if (resumeCallId) wsUrl.searchParams.set('resume_call_id', resumeCallId);
    return wsUrl;
  }

  private wireSocket(ws: WebSocket): void {
    ws.addEventListener('message', ev => {
      this.onServerEvent(ev);
    });
    ws.addEventListener('close', ev => {
      this.onSocketClose(ws, ev.code);
    });
    ws.addEventListener('error', () => {
      logger.error('OpenAIRealtimeService: ws error');
    });
  }

  /**
   * A clean 1000 (or a pre-start failure) ends the call as before; an
   * abnormal loss mid-interview enters the resume loop instead of
   * killing a 20-minute interview on a wifi blip (LUM-176 #3). 4011
   * is the relay reporting ITS side died (OpenAI socket loss) — the
   * call row is already closed, so resume would only 410; surface the
   * failure instead of a silent wrap-up.
   */
  private onSocketClose(ws: WebSocket, code: number): void {
    if (ws !== this.ws) return; // superseded socket — resume loop owns it
    logger.debug('OpenAIRealtimeService: ws closed', { code });
    if (!this.callStartFired) {
      this.teardown(undefined);
      this.callbacks.onError?.({ message: FRIENDLY_START_ERROR });
      return;
    }
    if (code === RELAY_FAILURE_CLOSE_CODE) {
      this.teardown(`relay-failure-${code}`);
      this.callbacks.onError?.({ message: FRIENDLY_START_ERROR });
      return;
    }
    if (code === 1000 || this.callEndFired) {
      this.teardown(`ws-close-${code}`);
      return;
    }
    void this.attemptResume(code);
  }

  /** Resolves with the relay's call id, or null on timeout. */
  private awaitRelayReady(timeoutMs: number): Promise<string | null> {
    const { promise, resolve } = Promise.withResolvers<string | null>();
    const timer = setTimeout(() => {
      this.onRelayReady = null;
      resolve(null);
    }, timeoutMs);
    this.onRelayReady = id => {
      clearTimeout(timer);
      this.onRelayReady = null;
      resolve(id);
    };
    return promise;
  }

  /**
   * Re-attach to the still-live relay DO after an abnormal socket loss.
   * The engine (mic + playback) never stops — chunks are dropped while
   * no socket is open, and the OpenAI session upstream keeps its
   * context, so a successful resume continues the interview mid-flow.
   * Dials until an absolute deadline just under the relay's 45s grace
   * (LUM-187), pausing while the browser knows it is offline and
   * retrying the moment the `online` event fires.
   */
  private async attemptResume(closeCode: number): Promise<void> {
    if (this.resuming) return;
    const { callId, sessionId } = this;
    if (!callId || !sessionId) {
      this.teardown(`ws-close-${closeCode}`);
      return;
    }
    this.resuming = true;
    this.ws = null;
    this.backpressureSince = 0;
    this.callbacks.onConnectionState?.('reconnecting');

    const deadline = Date.now() + RESUME_DEADLINE_MS;
    for (let attempt = 1; Date.now() < deadline; attempt++) {
      await delay(
        Math.min(
          RECONNECT_BASE_DELAY_MS * 2 ** (attempt - 1),
          RECONNECT_MAX_DELAY_MS,
        ),
      );
      if (this.callEndFired || !this.engine) {
        this.resuming = false;
        return; // user hung up while we were retrying
      }
      // A browser that KNOWS it is offline cannot dial — wait for the
      // online event (bounded by the deadline) instead of burning the
      // budget on doomed attempts.
      if (!navigator.onLine) {
        await awaitOnline(deadline - Date.now());
        if (this.callEndFired || !this.engine) {
          this.resuming = false;
          return;
        }
        if (!navigator.onLine) break; // deadline hit while still offline
      }
      const ticket = await this.mintTicket(sessionId);
      if (ticket === null) continue;
      if (this.callEndFired || !this.engine) {
        this.resuming = false;
        return;
      }
      const ws = new WebSocket(this.buildWsUrl(sessionId, ticket, callId));
      this.pendingResumeWs = ws;
      this.wireSocket(ws);
      const readyId = await this.awaitRelayReady(RESUME_READY_TIMEOUT_MS);
      this.pendingResumeWs = null;
      if (readyId && !this.callEndFired && this.engine) {
        this.ws = ws;
        this.lastDownlinkAt = Date.now();
        this.resuming = false;
        logger.debug('OpenAIRealtimeService: resumed', { attempt });
        this.callbacks.onConnectionState?.('reconnected');
        return;
      }
      try {
        // NOT 1000: the relay reads 1000 as a deliberate hang-up and
        // ends the call — an abandoned resume attempt would collapse
        // the whole 45s grace window (Codex crosscheck finding 1).
        ws.close(4002, 'resume-abandoned');
      } catch {
        // already dead
      }
      if (this.callEndFired) {
        this.resuming = false;
        return; // user hung up while this dial was in flight
      }
    }
    this.resuming = false;
    this.teardown(`connection-lost-${closeCode}`);
  }

  /**
   * Mic uplink with backpressure (LUM-176 #2): when the socket cannot
   * drain, queueing more audio only deepens the hole — drop chunks,
   * record the fact, and after a sustained stall force the socket
   * closed so the resume path takes over.
   */
  private sendMicChunk(b64: string): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (ws.bufferedAmount > BACKPRESSURE_MAX_BUFFERED_BYTES) {
      this.micDropped += 1;
      if (this.backpressureSince === 0) this.backpressureSince = now;
      if (now - this.lastBackpressureTelemetry > 5_000) {
        this.lastBackpressureTelemetry = now;
        // Tiny frame; it queues behind the backlog and surfaces in
        // forensics if (and only if) the link ever drains.
        this.send({ type: 'dd.client.backpressure', n: this.micDropped });
      }
      if (now - this.backpressureSince > BACKPRESSURE_STALL_MS) {
        logger.warn('OpenAIRealtimeService: backpressure stall', {
          dropped: this.micDropped,
        });
        this.backpressureSince = 0;
        try {
          ws.close(4008, 'backpressure-stall');
        } catch {
          // close raced; the close handler drives recovery either way
        }
      }
      return;
    }
    this.backpressureSince = 0;
    try {
      ws.send(
        JSON.stringify({ type: 'input_audio_buffer.append', audio: b64 }),
      );
    } catch (err) {
      logger.warn('OpenAIRealtimeService: mic send failed', { err });
    }
  }

  async stop(): Promise<void> {
    // `resuming` counts as active: hanging up mid-reconnect must land
    // the normal end path, not no-op while the resume loop spins.
    if (!this.ws && !this.resuming) return;
    logger.debug('OpenAIRealtimeService: stopping call');
    this.teardown('user_ended');
  }

  setInputDevice(deviceId: string): void {
    this.inputDeviceId = deviceId;
    this.engine?.setInputDevice(deviceId);
  }

  setOutputDevice(deviceId: string): void {
    this.outputDeviceId = deviceId;
    this.engine?.setOutputDevice(deviceId);
  }

  cleanup(): void {
    this.teardown(this.callStartFired ? 'cleanup' : undefined);
    this.callbacks = {};
    this.isInitialized = false;
  }

  private teardown(endReason: string | undefined): void {
    this.cancelScheduledResponse?.();
    this.cancelScheduledResponse = null;
    this.resuming = false;
    this.stopDownlinkWatchdog();
    const pending = this.pendingResumeWs;
    this.pendingResumeWs = null;
    if (pending && pending.readyState <= WebSocket.OPEN) {
      try {
        pending.close(4002, 'client-teardown');
      } catch {
        // already closing
      }
    }
    const ws = this.ws;
    this.ws = null;
    if (ws && ws.readyState <= WebSocket.OPEN) {
      try {
        ws.close(1000, 'client-teardown');
      } catch {
        // already closing
      }
    }
    this.engine?.stop();
    this.engine = null;
    if (endReason && this.callStartFired && !this.callEndFired) {
      this.callEndFired = true;
      this.callbacks.onCallEnd?.({ reason: endReason });
    }
  }

  private send(payload: Record<string, unknown>): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify(payload));
    } catch (err) {
      logger.warn('OpenAIRealtimeService: send failed', { err });
    }
  }

  /** Feed one event through the turn arbiter and act on its decision.
   *  (A duration-based sustained-speech gate lived here between builds
   *  v8-v9 and was removed: it kept cutting playback on long hums —
   *  live calls 8/9 — while the per-item words path already handles
   *  genuine stops reliably at end of utterance.) */
  private feedArbiter(event: Record<string, unknown>): void {
    this.applyDecision(this.bargeInGate.observe(event));
  }

  /** Execute a turn decision, from observe() or an echo discard. */
  private applyDecision(decision: TurnDecision): void {
    if (decision.cancelPendingResponse) {
      this.cancelScheduledResponse?.();
      this.cancelScheduledResponse = null;
    }
    if (!decision.cancel && !decision.clearBuffer && !decision.createResponse) {
      return;
    }
    logger.debug('OpenAIRealtimeService: turn decision', { ...decision });
    if (decision.cancel) {
      this.send({ type: 'response.cancel' });
    }
    if (decision.clearBuffer) {
      this.silencePlayback('turn decision');
    }
    if (decision.createResponse) {
      // Delay chosen by the arbiter from utterance completeness: a
      // complete sentence gets a fast reply; a trailing fragment gets a
      // long grace window. Resumed speech cancels either. Each
      // scheduled/fired/cancelled triple shares a decision id in the
      // forensic timeline (LUM-176 #5) so debounce behaviour is
      // reconstructable per call.
      this.cancelScheduledResponse?.();
      const decisionId = ++this.decisionSeq;
      this.send({
        type: 'dd.client.decision',
        decision_id: decisionId,
        action: 'scheduled',
        ms: decision.responseDelayMs,
      });
      const timer = setTimeout(() => {
        this.cancelScheduledResponse = null;
        this.send({
          type: 'dd.client.decision',
          decision_id: decisionId,
          action: 'fired',
        });
        this.setAgentState('thinking');
        this.send({ type: 'response.create' });
      }, decision.responseDelayMs);
      this.cancelScheduledResponse = () => {
        clearTimeout(timer);
        this.send({
          type: 'dd.client.decision',
          decision_id: decisionId,
          action: 'cancelled',
        });
      };
    }
  }

  /** Instant local silence + honest truncate + arbiter state sync. */
  private silencePlayback(reason: string): void {
    const engine = this.engine;
    if (!engine) return;
    const { itemId, playedMs } = engine.interruptPlayback();
    logger.debug('OpenAIRealtimeService: playback silenced', { reason });
    this.setAgentState('listening');
    if (itemId) {
      this.send({
        type: 'conversation.item.truncate',
        item_id: itemId,
        content_index: 0,
        audio_end_ms: Math.max(0, Math.round(playedMs)),
      });
    }
    // Keep the arbiter's playback state truthful — the engine will not
    // emit a stopped event after an interrupt.
    this.bargeInGate.observe({ type: 'output_audio_buffer.cleared' });
  }

  /** Emit deduped presence transitions to the UI (LUM-133). */
  private setAgentState(state: RealtimeAgentState): void {
    if (state === this.agentState) return;
    this.agentState = state;
    this.callbacks.onAgentState?.(state);
  }

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: single event-dispatch switch
  private onServerEvent(ev: MessageEvent): void {
    let event: Record<string, unknown>;
    try {
      event = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
    } catch {
      return;
    }

    const type = event.type as string | undefined;
    if (!type) return;

    // ANY frame proves the downlink is alive (LUM-187) — including
    // the relay's dd.relay.ping, whose only job is to arrive.
    this.lastDownlinkAt = Date.now();
    if (type === 'dd.relay.ping') return;

    // Audio deltas go straight to the engine; they are high-frequency
    // and carry no turn-policy signal.
    if (type === 'response.output_audio.delta') {
      const delta = event.delta as string | undefined;
      const itemId =
        typeof event.item_id === 'string' ? event.item_id : 'unknown-item';
      if (delta && this.engine) {
        this.engine.enqueuePlayback(delta, itemId);
      }
      return;
    }

    // STT prompt-echo guard (live call 6): drop hallucinated
    // vocabulary-list "utterances" before they reach turn policy or the
    // UI, and delete the item so the model's context stays clean.
    if (type === 'conversation.item.input_audio_transcription.completed') {
      const text = typeof event.transcript === 'string' ? event.transcript : '';
      if (isVocabularyEcho(text, this.effectiveVocabulary)) {
        logger.debug('OpenAIRealtimeService: vocab echo dropped');
        if (typeof event.item_id === 'string') {
          // Release the arbiter's pending slot — a stuck entry blocks
          // every later reply (live call 9 went silent this way). The
          // returned decision can carry a reply OWED to an earlier real
          // utterance whose turn was deferred to this phantom (call 11).
          this.applyDecision(this.bargeInGate.discardPending(event.item_id));
          this.send({
            type: 'conversation.item.delete',
            item_id: event.item_id,
          });
        }
        return;
      }
    }

    this.feedArbiter(event);

    switch (type) {
      case 'dd.relay.ready': {
        const callId = typeof event.call_id === 'string' ? event.call_id : null;
        this.callId = callId;
        if (typeof event.vocabulary === 'string') {
          this.effectiveVocabulary = event.vocabulary;
        }
        if (event.resumed === true) {
          // Mid-call re-attach (LUM-176 #3): the OpenAI session never
          // died — no greeting, no onCallStart, just carry on.
          if (callId) this.onRelayReady?.(callId);
          return;
        }
        if (!this.callStartFired) {
          this.callStartFired = true;
          this.callbacks.onCallStart?.({ callId: callId ?? undefined });
        }
        // OpenAI Realtime won't speak unprompted on a fresh session —
        // kick off the assistant's first turn. The system prompt's
        // "Greeting" section dictates what it says.
        this.setAgentState('thinking');
        this.send({ type: 'response.create' });
        if (callId) this.onRelayReady?.(callId);
        return;
      }
      case 'response.audio_transcript.delta':
      case 'response.output_audio_transcript.delta': {
        const delta = event.delta as string | undefined;
        if (delta) this.partialAssistantTranscript += delta;
        return;
      }
      case 'response.audio_transcript.done':
      case 'response.output_audio_transcript.done': {
        const text =
          (event.transcript as string | undefined) ??
          this.partialAssistantTranscript;
        if (text.trim()) {
          this.callbacks.onTranscript?.({
            role: 'assistant',
            text,
            timestamp: new Date(),
          });
        }
        this.partialAssistantTranscript = '';
        return;
      }
      case 'conversation.item.input_audio_transcription.completed': {
        const text = event.transcript as string | undefined;
        if (text?.trim()) {
          this.callbacks.onTranscript?.({
            role: 'user',
            text,
            timestamp: new Date(),
          });
        }
        return;
      }
      case 'response.output_item.done': {
        const topicId = markedTopicIdFromEvent(event);
        if (topicId) this.callbacks.onTopicMarked?.(topicId);
        return;
      }
      case 'error': {
        const errObj = event.error as
          | { message?: string; type?: string }
          | undefined;
        this.callbacks.onError?.({
          message: errObj?.message,
          error: errObj,
        });
        return;
      }
      default:
        logger.debug('OpenAIRealtimeService: event', { type });
    }
  }
}

export function createOpenAIRealtimeService(): OpenAIRealtimeService {
  return new OpenAIRealtimeService();
}

export default OpenAIRealtimeService;
